import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { User, Lock, Mail, LogOut, Shield, Download, Bot, Lock as LockIcon, Info } from 'lucide-react';
import AdminWaitlistViewer from '@/components/AdminWaitlistViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountSettings() {
  const { user, token, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // Automation preferences (subset of /api/paper-trading/settings — user-safe controls only)
  const [autoSettings, setAutoSettings] = useState(null);
  const [tempMaxTrades, setTempMaxTrades] = useState(5);
  const [savingAuto, setSavingAuto] = useState(false);

  const loadAutoSettings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAutoSettings(data);
        setTempMaxTrades(data.max_trades_per_day ?? 5);
      }
    } catch {
      /* silent — user can still see profile */
    }
  }, [token]);

  useEffect(() => {
    loadAutoSettings();
  }, [loadAutoSettings]);

  const persistAutoSettings = async (patch) => {
    if (!autoSettings) return;
    setSavingAuto(true);
    try {
      const body = {
        enabled: autoSettings.enabled,
        max_trades_per_day: autoSettings.max_trades_per_day,
        min_score_threshold: autoSettings.min_score_threshold,
        asset_thresholds: autoSettings.asset_thresholds,
        emergency_stop: autoSettings.emergency_stop,
        ...patch,
      };
      const res = await fetch(`${API_URL}/api/paper-trading/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setAutoSettings((prev) => ({ ...prev, ...patch }));
        toast.success('Automation preferences updated');
      } else {
        toast.error('Failed to update preferences');
      }
    } catch {
      toast.error('Failed to update preferences');
    } finally {
      setSavingAuto(false);
    }
  };

  const handleToggleAutomation = (checked) => {
    persistAutoSettings({ enabled: checked });
  };

  const handleSaveMaxTrades = () => {
    let val = parseInt(tempMaxTrades, 10);
    if (!val || val < 1) val = 1;
    if (val > 20) val = 20;
    setTempMaxTrades(val);
    if (val !== autoSettings?.max_trades_per_day) {
      persistAutoSettings({ max_trades_per_day: val });
    }
  };

  const handleExportWaitlist = async () => {
    setExportingCsv(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/waitlist/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to export waitlist');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'waitlist_export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Waitlist exported');
    } catch (err) {
      toast.error('Failed to export waitlist');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoadingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          current_password: currentPassword, 
          new_password: newPassword 
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail || !emailPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoadingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/update-email`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          new_email: newEmail, 
          password: emailPassword 
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('Email updated successfully. Please log in again.');
        logout();
      } else {
        toast.error(data.detail || 'Failed to update email');
      }
    } catch (error) {
      toast.error('Failed to update email');
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground" data-testid="automation-prefs-title">Automation Preferences</h1>
        <p className="text-muted-foreground">Control how Trading Pilot 360 trades for you, and manage your account.</p>
      </div>

      {/* Automation Controls — user-safe subset */}
      <Card className="p-6 border-border/40 bg-card" data-testid="automation-controls-card">
        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Automation Controls</h2>
            <p className="text-sm text-muted-foreground">Turn automation on or off and cap how many trades it can take per day.</p>
          </div>
        </div>

        {!autoSettings ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-5">
            {/* On/Off toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/20 border border-border/40">
              <div>
                <Label htmlFor="auto-on-toggle" className="font-semibold text-foreground">
                  Automation {autoSettings.enabled ? 'ON' : 'OFF'}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {autoSettings.enabled
                    ? 'New qualifying signals will open paper trades automatically.'
                    : 'No new trades will be opened automatically.'}
                </p>
              </div>
              <Switch
                id="auto-on-toggle"
                checked={!!autoSettings.enabled}
                onCheckedChange={handleToggleAutomation}
                disabled={savingAuto || autoSettings.emergency_stop}
                data-testid="automation-on-toggle"
              />
            </div>

            {/* Max trades per day */}
            <div className="space-y-2">
              <Label htmlFor="max-trades" className="font-semibold">Max trades per day</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="max-trades"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min="1"
                  max="20"
                  value={tempMaxTrades}
                  onChange={(e) => setTempMaxTrades(e.target.value)}
                  onBlur={handleSaveMaxTrades}
                  className="bg-secondary/20 border-border/50 max-w-[120px]"
                  data-testid="max-trades-pref-input"
                />
                <p className="text-xs text-muted-foreground">
                  {autoSettings.trades_today ?? 0} taken today
                </p>
              </div>
              <p className="text-xs text-muted-foreground">Hard cap. Automation stops opening new trades once this is reached.</p>
            </div>

            {/* Risk per trade — locked */}
            <div
              className="space-y-2 p-4 rounded-lg bg-secondary/10 border border-dashed border-border/40 cursor-not-allowed"
              title="Managed automatically for optimal performance"
              data-testid="risk-per-trade-locked"
            >
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-muted-foreground flex items-center gap-2">
                  <LockIcon className="w-3.5 h-3.5" />
                  Risk per trade
                </Label>
                <span className="font-mono text-sm text-muted-foreground">
                  {(autoSettings.risk_percent ?? 1).toFixed(1)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Info className="w-3 h-3" />
                Managed automatically for optimal performance.
              </p>
            </div>

            {/* Position sizing — locked */}
            <div
              className="space-y-2 p-4 rounded-lg bg-secondary/10 border border-dashed border-border/40 cursor-not-allowed"
              title="Managed automatically for optimal performance"
              data-testid="position-sizing-locked"
            >
              <Label className="font-semibold text-muted-foreground flex items-center gap-2">
                <LockIcon className="w-3.5 h-3.5" />
                Position sizing
              </Label>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Info className="w-3 h-3" />
                Calculated dynamically from your balance and stop-loss distance. Managed automatically for optimal performance.
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Account Info */}
      <Card className="p-6 border-border/40 bg-card">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Account Information</h2>
            <p className="text-sm text-muted-foreground">Your account details</p>
          </div>
        </div>
        
        <div className="space-y-3 pl-13">
          <div className="flex items-center space-x-2">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Email:</span>
            <span className="text-sm font-medium text-foreground">{user?.email}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Role:</span>
            <span className="text-sm font-medium text-foreground capitalize">{user?.role}</span>
          </div>
        </div>
      </Card>

      {/* Admin: Waitlist Export */}
      {user?.role === 'admin' && (
        <Card className="p-6 border-border/40 bg-card" data-testid="admin-waitlist-export-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Download className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Waitlist Export</h2>
                <p className="text-sm text-muted-foreground">
                  Download all waitlist signups (email, name, source, consent)
                </p>
              </div>
            </div>
            <Button
              onClick={handleExportWaitlist}
              disabled={exportingCsv}
              className="bg-primary hover:bg-primary/90"
              data-testid="export-waitlist-btn"
            >
              {exportingCsv ? 'Exporting...' : 'Export Waitlist CSV'}
            </Button>
          </div>
        </Card>
      )}

      {/* Admin: Waitlist Viewer */}
      {user?.role === 'admin' && <AdminWaitlistViewer token={token} />}

      {/* Change Password */}
      <Card className="p-6 border-border/40 bg-card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
            <Lock className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Change Password</h2>
            <p className="text-sm text-muted-foreground">Update your password</p>
          </div>
        </div>
        
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="bg-secondary/20 border-border/50"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="bg-secondary/20 border-border/50"
              required
              minLength={8}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-secondary/20 border-border/50"
              required
              minLength={8}
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loadingPassword}
            className="bg-primary hover:bg-primary/90"
          >
            {loadingPassword ? 'Updating...' : 'Update Password'}
          </Button>
        </form>
      </Card>

      {/* Change Email */}
      <Card className="p-6 border-border/40 bg-card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Change Email</h2>
            <p className="text-sm text-muted-foreground">Update your email address</p>
          </div>
        </div>
        
        <form onSubmit={handleChangeEmail} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="newEmail">New Email Address</Label>
            <Input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-secondary/20 border-border/50"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="emailPassword">Current Password</Label>
            <Input
              id="emailPassword"
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              placeholder="Verify with your password"
              className="bg-secondary/20 border-border/50"
              required
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loadingEmail}
            className="bg-primary hover:bg-primary/90"
          >
            {loadingEmail ? 'Updating...' : 'Update Email'}
          </Button>
        </form>
      </Card>

      {/* Logout */}
      <Card className="p-6 border-border/40 bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
              <LogOut className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Sign Out</h2>
              <p className="text-sm text-muted-foreground">Sign out of your account on this device</p>
            </div>
          </div>
          <Button 
            onClick={handleLogout}
            variant="destructive"
          >
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  );
}
